/**
 * Firestore Service
 * 
 * Direct utility functions for Firestore operations.
 * These provide a cleaner API over the Firebase SDK.
 * 
 * All functions support project-scoped collection paths via the projectId parameter.
 * When projectId is provided, collections are accessed at: projects/{projectId}/{collection}
 * When projectId is null/undefined, collections are accessed at root (legacy mode).
 */

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
} from 'firebase/firestore'
import { db } from '../firebase'

// Collection names
export const COLLECTIONS = {
  CHARACTERS: 'characters',
  LOCATIONS: 'locations',
  EVENTS: 'events',
  SHOTS: 'shots',
  IMAGES: 'images',
  CONTENT: 'content',
}

// Document IDs for singleton content
export const CONTENT_DOCS = {
  SYNOPSIS: 'synopsis',
}

/**
 * Build collection path with optional project scoping
 * @param {string|null} projectId - The project ID (null for legacy flat collections)
 * @param {string} collectionName - The collection name
 * @returns {string} - The full collection path
 */
export function getCollectionPath(projectId, collectionName) {
  if (!projectId) {
    // Legacy mode - use flat collection (backward compatibility)
    return collectionName
  }
  return `projects/${projectId}/${collectionName}`
}

/**
 * Add a new document to a collection
 * @param {string|null} projectId - The project ID (null for legacy flat collections)
 * @param {string} collectionName - The collection to add to
 * @param {object} data - The document data
 * @returns {Promise<string>} - The new document ID
 */
export async function addDocument(projectId, collectionName, data) {
  const collectionPath = getCollectionPath(projectId, collectionName)
  const docRef = await addDoc(collection(db, collectionPath), {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
  return docRef.id
}

/**
 * Get a single document by ID
 * @param {string|null} projectId - The project ID (null for legacy flat collections)
 * @param {string} collectionName - The collection name
 * @param {string} docId - The document ID
 * @returns {Promise<object|null>} - The document data or null
 */
export async function getDocument(projectId, collectionName, docId) {
  const collectionPath = getCollectionPath(projectId, collectionName)
  const docRef = doc(db, collectionPath, docId)
  const docSnap = await getDoc(docRef)
  
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() }
  }
  return null
}

/**
 * Get all documents from a collection
 * @param {string|null} projectId - The project ID (null for legacy flat collections)
 * @param {string} collectionName - The collection name
 * @param {object} options - Query options
 * @returns {Promise<array>} - Array of documents
 */
export async function getDocuments(projectId, collectionName, options = {}) {
  const { orderByField, orderDirection = 'asc', limitCount, whereClause } = options
  
  const collectionPath = getCollectionPath(projectId, collectionName)
  let q = collection(db, collectionPath)
  const constraints = []
  
  if (whereClause) {
    constraints.push(where(whereClause.field, whereClause.op, whereClause.value))
  }
  
  if (orderByField) {
    constraints.push(orderBy(orderByField, orderDirection))
  }
  
  if (limitCount) {
    constraints.push(limit(limitCount))
  }
  
  if (constraints.length > 0) {
    q = query(q, ...constraints)
  }
  
  const querySnapshot = await getDocs(q)
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
}

/**
 * Update a document
 * @param {string|null} projectId - The project ID (null for legacy flat collections)
 * @param {string} collectionName - The collection name
 * @param {string} docId - The document ID
 * @param {object} data - The fields to update
 * @returns {Promise<void>}
 */
export async function updateDocument(projectId, collectionName, docId, data) {
  const collectionPath = getCollectionPath(projectId, collectionName)
  const docRef = doc(db, collectionPath, docId)
  await updateDoc(docRef, {
    ...data,
    updatedAt: serverTimestamp(),
  })
}

/**
 * Delete a document
 * @param {string|null} projectId - The project ID (null for legacy flat collections)
 * @param {string} collectionName - The collection name
 * @param {string} docId - The document ID
 * @returns {Promise<void>}
 */
export async function deleteDocument(projectId, collectionName, docId) {
  const collectionPath = getCollectionPath(projectId, collectionName)
  const docRef = doc(db, collectionPath, docId)
  await deleteDoc(docRef)
}

/**
 * Get documents with a specific link
 * Useful for finding related entities (e.g., all events for a character)
 * @param {string|null} projectId - The project ID (null for legacy flat collections)
 * @param {string} collectionName - The collection to search
 * @param {string} linkField - The array field to search in
 * @param {string} linkedId - The ID to search for
 * @returns {Promise<array>}
 */
export async function getLinkedDocuments(projectId, collectionName, linkField, linkedId) {
  const collectionPath = getCollectionPath(projectId, collectionName)
  const q = query(
    collection(db, collectionPath),
    where(linkField, 'array-contains', linkedId)
  )
  const querySnapshot = await getDocs(q)
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
}

// Entity-specific helpers

/**
 * Create a new character
 * @param {string|null} projectId - The project ID (null for legacy flat collections)
 * @param {object} data - Character data
 */
export function createCharacter(projectId, data) {
  return addDocument(projectId, COLLECTIONS.CHARACTERS, {
    name: '',
    description: '',
    primaryImage: '',
    extraImages: [],
    attire: '',
    hairStyle: '',
    distinctFeatures: '',
    accessories: '',
    linkedLocations: [],
    linkedEvents: [],
    linkedShots: [],
    tier: 'B',
    ...data,
  })
}

/**
 * Create a new location
 * @param {string|null} projectId - The project ID (null for legacy flat collections)
 * @param {object} data - Location data
 */
export function createLocation(projectId, data) {
  return addDocument(projectId, COLLECTIONS.LOCATIONS, {
    name: '',
    description: '',
    primaryImage: '',
    extraImages: [],
    era: '',
    keyElements: '',
    atmosphereNotes: '',
    linkedCharacters: [],
    linkedEvents: [],
    linkedShots: [],
    tier: 'B',
    ...data,
  })
}

/**
 * Create a new event
 * @param {string|null} projectId - The project ID (null for legacy flat collections)
 * @param {object} data - Event data
 */
export function createEvent(projectId, data) {
  return addDocument(projectId, COLLECTIONS.EVENTS, {
    title: '',
    description: '',
    primaryImage: '',
    extraImages: [],
    date: null,
    era: '',
    showOnTimeline: true,
    linkedCharacters: [],
    linkedLocations: [],
    linkedShots: [],
    tier: 'B',
    ...data,
  })
}

/**
 * Create a new shot
 * @param {string|null} projectId - The project ID (null for legacy flat collections)
 * @param {object} data - Shot data
 */
export function createShot(projectId, data) {
  return addDocument(projectId, COLLECTIONS.SHOTS, {
    shotId: '',
    title: '',
    description: '',
    primaryImage: '',
    extraImages: [],
    durationSeconds: 0,
    startFrame: '',
    endFrame: '',
    layer: '',
    aspectRatio: '16:9',
    linkedCharacters: [],
    linkedLocations: [],
    linkedEvents: [],
    tier: 'B',
    ...data,
  })
}

/**
 * Create a new image metadata record
 * @param {string|null} projectId - The project ID (null for legacy flat collections)
 * @param {object} data - Image data
 */
export function createImage(projectId, data) {
  return addDocument(projectId, COLLECTIONS.IMAGES, {
    storageUrl: '',
    filename: '',
    prompt: '',
    model: '',
    notes: '',
    linkedEntityType: '',
    linkedEntityId: '',
    tier: 'B',
    ...data,
  })
}

/**
 * Save or update a singleton content document (like synopsis)
 * Uses setDoc with merge to create or update
 * @param {string|null} projectId - The project ID (null for legacy flat collections)
 * @param {string} docId - The content document ID
 * @param {object} data - The content data
 */
export async function saveContentDocument(projectId, docId, data) {
  const { setDoc, doc: docRef } = await import('firebase/firestore')
  const collectionPath = getCollectionPath(projectId, COLLECTIONS.CONTENT)
  const ref = docRef(db, collectionPath, docId)
  await setDoc(ref, {
    ...data,
    updatedAt: serverTimestamp(),
  }, { merge: true })
}

